import React, { useEffect, useState } from 'react';

import { Logger } from 'wtb-utils';
import { useTransactionContext } from 'context';
import { ANALYTICS_EVENTS } from 'constants.js';
import {
  orderSummaryStepEventHandler,
  personalInfoStepEventHandler,
  selectPaymentStepEventHandler,
  autogiroAccountSelectionEventHandler,
  autogiroAddBankAccountEventHandler,
  autogiroBankAccountEventHandler,
  autogiroBankListEventHandler,
  autogiroBillingInformationEventHandler,
  autogiroConnectingBankEventHandler,
  autogiroMainBanksEventHandler,
  autogiroSummaryEventHandler,
  invoiceBillingEditorEventHandler,
  invoiceBillingSelectorEventHandler,
  signOpenEventHandler,
  signWaitEventHandler,
  emailValidationScreenEventHandler,
  errorScreenEventHandler,
  rejectedScreenEventHandler,
  successScreenEventHandler,
  campaignEventHandler,
  merchantEventHandler,
  personalInfoTimingEventHandler,
  selectedPaymentPlanEventHandler,
  autogiroTinkSelectBankAccountButtonEventHandler,
  autogiroFinsharkSelectBankAccountButtonEventHandler,
  retryScreenEventHandler
} from './wtbEventHandlers';

// Analytics events and their WTB handlers
const WTB_EVENT_HANDLERS = [
  [ ANALYTICS_EVENTS.ORDER_SUMMARY_STEP, orderSummaryStepEventHandler ],
  [ ANALYTICS_EVENTS.PERSONAL_INFORMATION_STEP, personalInfoStepEventHandler ],
  [ ANALYTICS_EVENTS.SELECT_PAYMENT_PLAN_STEP, selectPaymentStepEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.ACCOUNT_SELECTION, autogiroAccountSelectionEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.START_TINK_BUTTON, autogiroTinkSelectBankAccountButtonEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.START_FINSHARK_BUTTON, autogiroFinsharkSelectBankAccountButtonEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.ADD_BANK_ACCOUNT, autogiroAddBankAccountEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.ACCOUNT_FORM, autogiroBankAccountEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.BANK_LIST, autogiroBankListEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.BILLING, autogiroBillingInformationEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.CONNECTING_BANK, autogiroConnectingBankEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.MAIN_BANKS, autogiroMainBanksEventHandler ],
  [ ANALYTICS_EVENTS.AUTOGIRO.SUMMARY, autogiroSummaryEventHandler ],
  [ ANALYTICS_EVENTS.RETRY, retryScreenEventHandler ],
  [ ANALYTICS_EVENTS.INVOICE.BILLING_EDITOR, invoiceBillingEditorEventHandler ],
  [ ANALYTICS_EVENTS.INVOICE.BILLING_SELECTOR, invoiceBillingSelectorEventHandler ],
  [ ANALYTICS_EVENTS.SIGN.OPEN, signOpenEventHandler ],
  [ ANALYTICS_EVENTS.SIGN.WAIT, signWaitEventHandler ],
  [ ANALYTICS_EVENTS.EMAIL_VALIDATION, emailValidationScreenEventHandler ],
  [ ANALYTICS_EVENTS.ERROR, errorScreenEventHandler ],
  [ ANALYTICS_EVENTS.REJECTED, rejectedScreenEventHandler ],
  [ ANALYTICS_EVENTS.SUCCESS, successScreenEventHandler ],
  [ ANALYTICS_EVENTS.CAMPAIGN, campaignEventHandler ],
  [ ANALYTICS_EVENTS.MERCHANT, merchantEventHandler ],
  [ ANALYTICS_EVENTS.PERSONAL_INFO_TIMING, personalInfoTimingEventHandler ],
  [ ANALYTICS_EVENTS.SELECTED_PAYMENT_PLAN, selectedPaymentPlanEventHandler ]
];

// Gets the raw analytics file and takes out the conditionally added scripts depending on the current conditions
const parseAnalyticsScript = (_analyticsScript, data) => {
  let result = _analyticsScript;

  const [ key, value ] = Object.entries(data)[0];

  let keys = _analyticsScript
    .match(new RegExp(`(<!-- ?if:${key} ?= ?)(.*?)(?= ?-->)`, 'gis'));

  if (keys && keys.length > 0) {
    keys = keys.map((_key) => _key.replace(new RegExp(`<!-- ?if:${key} ?= ?`, 'gis'), ''));
  }

  (keys || []).forEach((_name) => {
    if (_name !== value) {
      result = result.replace(
        result.match(new RegExp(`<!-- ?if:${key} ?= ?${_name} ?-->(.*?)<!-- ?endif ?-->`, 'gis')),
        ''
      );
    }
  });

  result = result.replace(new RegExp(`<!-- ?if:${key} ?= ?${value} ?-->`, 'gis'), '');
  result = result.replace(new RegExp('<!-- ?endif ?-->', 'gis'), '');

  return result;
};

const Analytics = () => {
  const { analyticsScript, order } = useTransactionContext();

  const [ initialized, setInitialized ] = useState(false);

  useEffect(() => {
    if (analyticsScript && order.items?.length && !initialized) {
      setInitialized(true);

      const parsedAnalyticsScript = parseAnalyticsScript(analyticsScript, { articleName: order.items[0].description });

      const scripts = parsedAnalyticsScript.match(/<script[\s\S]*?>[\s\S]*?<\/script>/gi);

      const isExternalScript = (script) => /><\/script>/.test(script);
      scripts.forEach((script) => {
        const scriptElement = document.createElement('script');

        if (isExternalScript(script)) {
          let src = script.match(/src=".*"/g);
          src = (src[0] || '').replace('src="', '').replace(/"/g, '');

          scriptElement.src = src;
          scriptElement.async = true;
        } else {
          const code = script.replace('<script>', '').replace('</script>', '');

          scriptElement.text = code;
        }

        document.body.appendChild(scriptElement);
      });

      Logger.info('Analytics script added');
    }
  }, [ analyticsScript, initialized, order.items, setInitialized ]);

  useEffect(() => {
    // Allow disabling the sending of WTB analytics events to Google Analytics depending on environment
    if (process.env.REACT_APP_USE_WTB_ANALYTICS === 'false') return undefined;

    WTB_EVENT_HANDLERS.forEach(([ event, handler ]) => {
      document.addEventListener(event, handler);
    });

    return () => {
      WTB_EVENT_HANDLERS.forEach(([ event, handler ]) => {
        document.removeEventListener(event, handler);
      });
    };
  }, []);

  return (
    <div />
  );
};

export default Analytics;
